import { type ReactNode, useEffect, useId, useRef, useState } from "react";

export default function SelectMenu({
  label,
  value,
  options,
  onChange,
  icon,
  iconOnly = false,
}: {
  label: string;
  value: string;
  options: { value: string; label: string; lang?: string }[];
  onChange: (value: string) => void;
  icon?: ReactNode;
  iconOnly?: boolean;
}) {
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const typed = useRef({ text: "", time: 0 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  function expand() {
    if (!options.length) return;
    setActive(
      Math.max(
        0,
        options.findIndex((option) => option.value === value),
      ),
    );
    typed.current = { text: "", time: 0 };
    setOpen(true);
  }

  function select(next: string) {
    setOpen(false);
    trigger.current?.focus();
    if (next !== value) onChange(next);
  }

  return (
    <span
      className={`select-menu${iconOnly ? " select-menu-icon-only" : ""}`}
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        type="button"
        role="combobox"
        ref={trigger}
        className="select-menu-trigger"
        aria-label={label}
        title={iconOnly ? label : undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? `${id}-list` : undefined}
        aria-activedescendant={open && options[active] ? `${id}-${active}` : undefined}
        onClick={() => (open ? setOpen(false) : expand())}
        onKeyDown={(event) => {
          if (event.key === "Escape" || event.key === "Tab") {
            setOpen(false);
            if (event.key === "Escape" && open) {
              event.preventDefault();
              event.stopPropagation();
            }
            return;
          }
          if (["ArrowDown", "ArrowUp", "Home", "End", "Enter", " "].includes(event.key)) {
            event.preventDefault();
            if (!open) {
              expand();
              return;
            }
            if (event.key === "Enter" || event.key === " ") {
              if (options[active]) select(options[active].value);
            } else if (event.key === "Home") setActive(0);
            else if (event.key === "End") setActive(options.length - 1);
            else setActive(Math.max(0, Math.min(options.length - 1, active + (event.key === "ArrowDown" ? 1 : -1))));
          } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault();
            const now = Date.now();
            const text = (now - typed.current.time < 700 ? typed.current.text : "") + event.key.toLowerCase();
            typed.current = { text, time: now };
            const index = options.findIndex((option) => option.label.toLowerCase().startsWith(text));
            if (index >= 0) {
              setActive(index);
              setOpen(true);
            }
          }
        }}
      >
        <span className={icon ? "select-menu-label-with-icon" : undefined} lang={selected?.lang}>
          {icon}
          {!iconOnly && (selected?.label || label)}
        </span>
        {!iconOnly && (
          <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" className="select-menu-chevron">
            <path
              d="m4 6 4 4 4-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
      {open && (
        <span id={`${id}-list`} role="listbox" aria-label={label} className="select-menu-options">
          {options.map((option, index) => (
            <button
              type="button"
              role="option"
              lang={option.lang}
              id={`${id}-${index}`}
              key={option.value}
              tabIndex={-1}
              aria-selected={option.value === value}
              className={`select-menu-option${active === index ? " is-highlighted" : ""}`}
              onMouseDown={(event) => event.preventDefault()}
              onMouseMove={() => setActive(index)}
              onClick={() => select(option.value)}
            >
              <span aria-hidden="true" className="select-menu-check">
                {option.value === value ? "✓" : ""}
              </span>
              <span>{option.label}</span>
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
